import React, { ReactNode, useMemo } from "react";
import {
  OptionsObject,
  SnackbarKey,
  SnackbarMessage,
  useSnackbar,
} from "notistack";
import {
  Alert,
  AlertTitle,
  Box,
  Palette,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CancelRounded,
  CheckCircleRounded,
  Close,
  ErrorRounded,
  InfoRounded,
} from "@mui/icons-material";

export type NotificationType =
  | "error"
  | "success"
  | "warning"
  | "info"
  | "diana";

export type TypedNotificationArgs = {
  message: ReactNode;
  description?: ReactNode;
  link?: string | null | undefined;
};

export type CreateTypedNotificationArgs = {
  type: NotificationType;
  palette: Palette;
  closeSnackbar: (key?: SnackbarKey) => void;
  enqueue: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
};

export interface CreateNotificationContentProps extends TypedNotificationArgs {
  type: NotificationType;
  palette: Palette;
  sx: SxProps;
  handleClose: (e: React.BaseSyntheticEvent) => void;
}

export const createNotificationContent = ({
  type,
  message,
  description,
  palette,
  sx,
  handleClose,
}: CreateNotificationContentProps): JSX.Element => {
  const { error, warning, primary, text, info } = palette;

  return (
    <Alert
      severity={type === "diana" ? "success" : type}
      sx={sx}
      action={
        <Close
          data-testid="closeIcon"
          sx={{ "&:hover": { cursor: "pointer" } }}
          fontSize="small"
          onClick={handleClose}
        />
      }
      iconMapping={{
        error: <CancelRounded fontSize="small" sx={{ color: error.main }} />,
        info: <InfoRounded fontSize="small" sx={{ color: info.main }} />,
        success: (
          <CheckCircleRounded fontSize="small" sx={{ color: primary.main }} />
        ),
        warning: <ErrorRounded fontSize="small" sx={{ color: warning.main }} />,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        {description ? (
          <Box>
            <AlertTitle>
              <Typography fontWeight={500} sx={{ color: text.primary }}>
                {message}
              </Typography>
            </AlertTitle>
            <Typography sx={{ color: text.secondary }}>description</Typography>
          </Box>
        ) : (
          <Typography fontWeight={500} sx={{ color: text.primary }}>
            {message}
          </Typography>
        )}
      </Box>
    </Alert>
  );
};

export function createTypedNotification({
  type,
  enqueue,
  palette,
  closeSnackbar,
}: CreateTypedNotificationArgs): (args: TypedNotificationArgs) => SnackbarKey {
  const sx = {
    width: "100%",
    minWidth: "320px",
    maxWidth: "450px",
    backgroundColor: palette.divider,
    padding: "16px",
    ".MuiAlert-icon": {
      padding: 0,
      marginRight: "16px",
    },
    ".MuiAlert-message": {
      padding: 0,
    },
  } as SxProps;

  const handleClose = (e: React.BaseSyntheticEvent) => {
    e.stopPropagation();
    closeSnackbar();
  };

  return (args) => {
    const props = {
      ...args,
      sx,
      type,
      palette,
      handleClose,
    };
    return enqueue("", {
      content: createNotificationContent({ ...props }),
      persist: type === "diana",
    });
  };
}

export function useNotifications() {
  const { palette } = useTheme();
  const { enqueueSnackbar: enqueue, closeSnackbar } = useSnackbar();
  const commonProps = { palette, closeSnackbar, enqueue };
  return useMemo(
    () => ({
      success: createTypedNotification({ type: "success", ...commonProps }),
      diana: createTypedNotification({ type: "diana", ...commonProps }),
      error: createTypedNotification({ type: "error", ...commonProps }),
      warning: createTypedNotification({ type: "warning", ...commonProps }),
      info: createTypedNotification({ type: "info", ...commonProps }),
      close: closeSnackbar,
    }),
    // eslint-disable-next-line
    [closeSnackbar, palette, enqueue]
  );
}
